import React, { useState } from 'react';
import axios from 'axios';
import './Payment.css';

const PaymentForm = () => {
  const [activeTab, setActiveTab] = useState('credit');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [rememberCard, setRememberCard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

 const config = {
  amount: 100,
  currency: 'USD',
  countryCode: 'KW',
  backendUrl: 'http://localhost:5000/api/execute-payment',
  paymentMethods: {
    credit: 2,    // Credit Card
    apple: 1,     // Apple Pay (common test value)
    google: 1     // Google Pay (common test value)
  }
};


  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    
    return parts.length ? parts.join(' ') : value;
  };

  const handleCardNumberChange = (e) => {
    setCardNumber(formatCardNumber(e.target.value));
  };

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    setExpiry(value);
  };

  const handleSubmit = async (e) => {
    if (activeTab === 'credit') e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const paymentData = {
        InvoiceAmount: config.amount,
        CurrencyIso: config.currency,
        PaymentMethodId: config.paymentMethods[activeTab],
        CustomerName: cardName || 'Test Customer',
        DisplayCurrencyIso: config.currency,
        MobileCountryCode: config.countryCode,
        CustomerMobile: '5555555555', // Test number
        CustomerEmail: '', // Test email
        Language: 'en',
        CallBackUrl: `${window.location.origin}/payment-success`,
        ErrorUrl: `${window.location.origin}/payment-error`,
        ...(activeTab === 'credit' && {
          CardNumber: cardNumber.replace(/\s/g, ''),
          CardHolderName: cardName,
          ExpiryDate: expiry,
          SecurityCode: cvv
        })
      };

      console.log('Sending payment:', paymentData);
      
      const response = await axios.post(config.backendUrl, paymentData, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 15000
      });

      console.log('Payment response:', response.data);

      if (response.data.IsSuccess) {
        if (activeTab === 'credit') {
          setSuccess('Payment successful! Processing...');
          setTimeout(() => {
            window.location.href = `${window.location.origin}/payment-success`;
          }, 2000);
        } else {
          window.location.href = response.data.Data.PaymentURL;
        }
      } else {
        setError(response.data.Message || 'Payment failed. Please try another method.');
      }
    } catch (err) {
      const errorDetails = err.response?.data?.details || 
                         err.response?.data?.Message ||
                         err.message;
      
      console.error('Payment failed:', {
        error: err,
        response: err.response,
        details: errorDetails
      });

      setError(`Payment error: ${errorDetails}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-container">
      <div className="payment-header">
        <h2>Checkout</h2>
        <p>Complete your purchase securely</p>
      </div>
      
      <div className="payment-tabs">
        <button 
          className={`tab-btn ${activeTab === 'credit' ? 'active' : ''}`}
          onClick={() => setActiveTab('credit')}
        >
          <i className="fas fa-credit-card"></i> Credit Card
        </button>
        <button 
          className={`tab-btn ${activeTab === 'google' ? 'active' : ''}`}
          onClick={() => setActiveTab('google')}
        >
          <i className="fab fa-google-pay"></i> Google Pay
        </button>
        <button 
          className={`tab-btn ${activeTab === 'apple' ? 'active' : ''}`}
          onClick={() => setActiveTab('apple')}
        >
          <i className="fab fa-apple-pay"></i> Apple Pay
        </button>
      </div>
      
      <div className="payment-content">
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Processing your payment...</p>
          </div>
        )}

        {error && (
          <div className="alert error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {success && (
          <div className="alert success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {activeTab === 'credit' ? (
          <form onSubmit={handleSubmit} className="credit-card-form">
            <div className="form-group">
              <label htmlFor="cardNumber">Card Number</label>
              <input
                type="text"
                id="cardNumber"
                value={cardNumber}
                onChange={handleCardNumberChange}
                placeholder="1234 5678 9012 3456"
                maxLength="19"
                required
              />
              <div className="card-icons">
                <i className="fab fa-cc-visa"></i>
                <i className="fab fa-cc-mastercard"></i>
                <i className="fab fa-cc-amex"></i>
                <i className="fab fa-cc-discover"></i>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="cardName">Cardholder Name</label>
              <input
                type="text"
                id="cardName"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expiry">Expiry Date</label>
                <input
                  type="text"
                  id="expiry"
                  value={expiry}
                  onChange={handleExpiryChange}
                  placeholder="MM/YY"
                  maxLength="5"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="cvv">CVV</label>
                <input
                  type="text"
                  id="cvv"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                  placeholder="123"
                  maxLength="4"
                  required
                />
                <span className="cvv-info">
                  <i className="fas fa-question-circle"></i>
                  <span className="tooltip">3 or 4 digits on back of card</span>
                </span>
              </div>
            </div>
            
            <div className="form-group remember-card">
              <input
                type="checkbox"
                id="rememberCard"
                checked={rememberCard}
                onChange={() => setRememberCard(!rememberCard)}
              />
              <label htmlFor="rememberCard">Save card for future payments</label>
            </div>
            
            <button type="submit" className="pay-btn" disabled={isLoading}>
              {isLoading ? 'Processing...' : 'Pay Now'}
            </button>
          </form>
        ) : (
          <div className="digital-wallet">
            <div className="wallet-info">
              <i className={`fab fa-${activeTab === 'apple' ? 'apple-pay' : 'google-pay'}`}></i>
              <h3>{activeTab === 'apple' ? 'Apple Pay' : 'Google Pay'}</h3>
              <p>You'll be redirected to complete your payment securely</p>
            </div>
            <button 
              className={`wallet-btn ${activeTab === 'apple' ? 'apple-pay-btn' : 'google-pay-btn'}`}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : `Pay with ${activeTab === 'apple' ? 'Apple Pay' : 'Google Pay'}`}
            </button>
          </div>
        )}

        <div className="secure-payment">
          <i className="fas fa-lock"></i>
          <span>Secure payment encrypted</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;